import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { CheckCircle, AlertCircle, FileText } from 'lucide-react'
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner'
import { Header } from '@/shared/components/layout/Header'
import { StatusBar } from '@/shared/components/layout/StatusBar'
import { getElectronicSignatureRecord } from '../services/electronicSigningApi'
import type { ElectronicSignatureRecord } from '../types'

/**
 * Vista pura — recibe el record por prop. La usa tanto este componente
 * (lookup por /se/:id) como UnifiedSignatureLookup (lookup por /v/:shortId).
 */
export function ElectronicSignatureView({ record }: { record: ElectronicSignatureRecord }) {
  return (
    <div className="min-h-screen flex flex-col pb-7 bg-gradient-to-br from-legal-50 via-white to-blue-50 dark:from-legal-900 dark:via-legal-800 dark:to-legal-900">
      <Header />
      <main className="flex-1 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white dark:bg-legal-800 rounded-xl shadow-lg border border-yellow-200 dark:border-yellow-800 overflow-hidden">
          <div className="bg-yellow-50 dark:bg-yellow-900/30 border-b border-yellow-200 dark:border-yellow-800 p-6">
            <div className="flex items-center gap-3">
              <CheckCircle className="text-yellow-600 dark:text-yellow-400" size={32} />
              <div>
                <h1 className="text-xl font-bold text-legal-900 dark:text-white">Firma Electrónica</h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">art. 5 Ley 25.506 Argentina</p>
              </div>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <Field label="Firmante" value={record.signer_name || 'Firmante'} />
            <Field label="Fecha de firma" value={new Date(record.signed_at).toLocaleString('es-AR')} />
            {record.original_filename && (
              <Field label="Documento original" value={record.original_filename} icon={<FileText size={14} />} />
            )}
            <Field label="Hash SHA-256 del PDF firmado" value={record.signed_hash} mono />
            <Field label="Hash SHA-256 del PDF original" value={record.original_hash} mono />
            <Field
              label="Posición de la firma"
              value={`Página ${record.page_number} - ${Math.round(record.position_width)}x${Math.round(record.position_height)} pt en (${Math.round(record.position_x)}, ${Math.round(record.position_y)})`}
            />
            <Field
              label="Tipo de firma"
              value={
                record.signature_type === 'text' ? 'Solo texto cursivo'
                  : record.signature_type === 'qr' ? 'Solo QR'
                    : 'Texto + QR'
              }
            />
          </div>

          <div className="bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800 p-5 text-sm text-yellow-900 dark:text-yellow-200">
            <p className="font-medium mb-1">Implicancia legal</p>
            <p className="text-xs">{record.legal_implication}</p>
          </div>
        </div>

        <p className="mt-4 text-xs text-center text-gray-500 dark:text-gray-400">
          Este registro es público y consultable en SignWiz. Para verificar integridad,
          calculá el SHA-256 del PDF y comparalo con los hashes mostrados arriba.
        </p>
      </div>
      </main>
      <StatusBar />
    </div>
  )
}

export default function ElectronicSignatureLookup() {
  const { id } = useParams<{ id: string }>()
  const [record, setRecord] = useState<ElectronicSignatureRecord | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    getElectronicSignatureRecord(id)
      .then(r => {
        if (!r) setError('Firma no encontrada')
        else setRecord(r)
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Error consultando firma'))
      .finally(() => setLoading(false))
  }, [id])

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col pb-7 bg-legal-50 dark:bg-legal-900">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <StatusBar />
      </div>
    )
  }
  if (error || !record) return <NotFound message={error || 'No existe una firma con ese ID.'} />
  return <ElectronicSignatureView record={record} />
}

export function NotFound({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex flex-col pb-7 bg-legal-50 dark:bg-legal-900">
      <Header />
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-legal-800 rounded-xl shadow-lg border border-legal-200 dark:border-legal-700 p-8 max-w-md text-center">
          <AlertCircle className="mx-auto text-red-500 mb-3" size={48} />
          <h1 className="text-xl font-bold text-legal-900 dark:text-white mb-2">No encontrada</h1>
          <p className="text-gray-600 dark:text-gray-300 text-sm">{message}</p>
        </div>
      </div>
      <StatusBar />
    </div>
  )
}

function Field({
  label, value, subtitle, mono, icon,
}: { label: string; value: string; subtitle?: string; mono?: boolean; icon?: React.ReactNode }) {
  return (
    <div className="bg-legal-50 dark:bg-legal-900/40 p-3 rounded-lg">
      <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
        {icon} {label}
      </p>
      <p className={`text-legal-900 dark:text-white ${mono ? 'font-mono text-xs break-all' : 'text-sm font-medium'}`}>
        {value}
      </p>
      {subtitle && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{subtitle}</p>}
    </div>
  )
}
